import { Component, ElementRef, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { isEqual } from 'underscore';
import { ToastrService } from 'ngx-toastr';

import { ApiShivaService } from '@core/apis/api-shiva.service';
import { ScannedEquipmentExportService } from '@core/components/scanned-equipment-list/scanned-equipment-export.service';
import { EQP_LOCATIONS, FormMode } from '@core/constants';
import { deepMerge } from '@core/helpers';
import {
  IAddReturnEquipmentPayload,
  IEquipment,
  IGenericListOptions,
  IListResult,
  ITinyEquipment,
  IWorkOrderItems
} from '@core/interfaces';
import { SignalsService } from '@core/services/signals.service';
import {
  PneyluAddReturnEquipmentModalComponent
} from '@views/work-order-items/work-order-items-metadata-templates/pneylu-add-return-equipment-modal.component';
import {
  PneyluEquipmentSelectModalComponent
} from '@views/work-order-items/work-order-items-metadata-templates/pneylu-equipment-select-modal.component';
import { EquipmentsModalComponent } from '@views/equipments/equipments-modal.component';

interface IEquipmentWithScanStatus extends ITinyEquipment {
  scan_status?: 'scanned' | 'added';
  manual_insert?: true | false;
}

interface IPneyluMetadata {
  reference_icma?: string;
  reference_dt?: string;
  equipments?: IEquipmentWithScanStatus[];
}

@Component({
  selector: 'app-pneylu-metadata',
  templateUrl: './pneylu-metadata.component.html',
  styleUrls: ['./pneylu-metadata.component.less']
})
export class PneyluMetadataComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('f', { static: true }) public f: NgForm;
  @ViewChild('serialNumberInput', { static: true }) public serialNumberInput?: ElementRef | undefined;

  @Input() public mode: FormMode = 'normal';
  @Input() public woi: IWorkOrderItems<IPneyluMetadata>;
  @Input() public woiSave: Function; // woi save method to call on seq update

  public readonly locationOptions: { [key: string]: string } = EQP_LOCATIONS;
  public loading: boolean = false;
  public items: IEquipmentWithScanStatus[] = [];
  public serialNumber: string | undefined;
  private metadataBackup: IPneyluMetadata;
  private woiEditionCancelledSignalHandler: Subscription;

  constructor(
    private readonly signalsService: SignalsService,
    private readonly ngbModal: NgbModal,
    private readonly apiShiva: ApiShivaService,
    private readonly toastr: ToastrService,
    public readonly scannedEquipmentExportService: ScannedEquipmentExportService,
  ) {}

  public ngOnInit(): void {
    this.woi.metadata = this.woi.metadata || {};

    this.woiEditionCancelledSignalHandler = this.signalsService.subscribe('woi-edition-cancelled', () => {
      this.woi.metadata = {
        ...this.woi.metadata,
        ...this.metadataBackup,
      };
      this._refreshEquipmentItems();
    });

    this._refreshEquipmentItems();
  }

  /**
   * This function will be called for every input change, so the mode will trigger a change too,
   * but we can't properly detect if the woi has changed because it's structure is too complex
   * Handle the metadata update from the parent view (ex: 'cancel' action that does a backup).
   */
  public ngOnChanges(changes: SimpleChanges): void {
    const previousMode: FormMode = changes?.mode?.previousValue;
    const currentMode: FormMode = changes?.mode?.currentValue;

    if (previousMode === 'normal' && currentMode === 'edition') {
      this.metadataBackup = JSON.parse(JSON.stringify(this.woi.metadata));
      setTimeout(() => this.serialNumberInput?.nativeElement.focus());
    }

    // If the equipment list changed, re-init the metadata
    const previousEquipments: IEquipmentWithScanStatus | undefined = changes?.woi?.previousValue?.metadata?.equipments;
    const currentEquipments: IEquipmentWithScanStatus | undefined = changes?.woi?.currentValue?.metadata?.equipments;
    // TODO underscore-removal custom method
    const equipmentListChanged: boolean = !isEqual(previousEquipments, currentEquipments);
    if (equipmentListChanged) {
      this._refreshEquipmentItems();
    }
  }

  public ngOnDestroy(): void {
    this.woiEditionCancelledSignalHandler.unsubscribe();
  }

  /**
   * Open the modal used to add existing equipment to the metadata.
   * This allows adding specific equipment that exists in the system.
   */
  public openAddExistingEquipmentModal(): void {
    const entityCode: string | undefined = this.woi.work_order?.entity?.code;
    const currentIds: number[] = this.items
      .filter((item: IEquipmentWithScanStatus) => !!item.id)
      .map((item: IEquipmentWithScanStatus) => item.id);

    const equipmentListOptions: IGenericListOptions = {
      filters: {
        limit: 10,
        entity__code: entityCode,
        id__nin: currentIds || null,
      },
      disabledButtons: {
        create: true,
        delete: true,
        export: true,
        exactSearch: true,
        assign: true,
        history: true,
        assignTags: true
      },
      disabledColumns: {
        selection: false,
        model__branded_name: true,
        entity__parent__name: true,
        entity__parent__code: true,
        entity__code: true,
        entity__customer_ref: true,
        entity__location__city: true,
        reserved_for__name: true,
        reserved_for__code: true,
        provider_order_number__or__provider_order__order_number: true,
        created_at: true,
        last_stock_activity: true,
        mac_address: true,
        owner: true,
        model__type_label: false,
        has_accounting_equipment: true,
        price_untaxed: true,
        tags: true,
        action: true,
      }
    };

    const modal = this.ngbModal.open(EquipmentsModalComponent, { size: 'lg' });
    modal.componentInstance.disabledButtons = equipmentListOptions.disabledButtons;
    modal.componentInstance.disabledColumns = equipmentListOptions.disabledColumns;
    modal.componentInstance.filters = equipmentListOptions.filters;
    modal.componentInstance.isMultiselectListModal = true;

    modal.result.then(
      (equipmentList: { [key: string]: IEquipment }) => {
        // Get the list of equipments that were selected
        const selectedEquipmentList: IEquipment[] = Object.values(equipmentList)
          .map((equipment: IEquipment) => this._getEquipmentPropertiesForMetadata(equipment));
        // Add them to the equipment list
        this.woi.metadata.equipments = [
          ...(this.woi.metadata.equipments || []),
          ...selectedEquipmentList,
        ];
        // Refresh the display
        this._refreshEquipmentItems();
        // Don't forget to save the updated list (this is not sync, so we do this last)
        this.woiSave();
      },
      () => {},
    );
  }

  /**
   * Open the modal used to add expected equipment to the metadata.
   * This only allows adding equipment by constructor, model, and quantity.
   * This will not add specific equipment that exists in the system.
   */
  public openAddEquipmentLineModal(serial_number?: string): void {
    const modal = this.ngbModal.open(PneyluAddReturnEquipmentModalComponent, { size: 'lg' });
    if (serial_number) {
      modal.componentInstance.serial_number = serial_number;
    }
    modal.result.then(
      (payload: IAddReturnEquipmentPayload) => {
        return this.apiShiva.work_order_items.addReturnEquipment(this.woi.id, payload)
          .then(() => this.signalsService.broadcast('woi-detail:refresh'));
      },
      () => {},
    );
  }

  /**
   * Remove the item at the provided index from the metadata equipment list.
   */
  public removeEquipmentRow(index: number): void {
    // Remove the index from the list of equipment in the metadata
    this.woi.metadata.equipments.splice(index, 1);
    // Update the display
    this._refreshEquipmentItems();
    // Don't forget to save the updated list (this is not sync, so we do this last)
    this.woiSave();
  }

  /**
   * Reset a line to unscanned.
   */
  public resetLineScan(index: number): void {
    const item = this.woi.metadata.equipments[index];
    if (!item.code) {
      if (item.serial_number !== null) {
        item.serial_number = null;
      }
      if (item.scan_status !== undefined) {
        item.scan_status = undefined;
      }
    } else if (item.scan_status === 'added') {
      if (item.serial_number !== null) {
        item.serial_number = null;
      }
      if (item.code !== null) {
        item.code = null;
      }
      if (item.scan_status !== undefined) {
        item.scan_status = undefined;
      }
    } else if (item.scan_status === 'scanned') {
      if (item.scan_status !== undefined) {
        item.scan_status = undefined;
      }
    }
    this.woi.metadata.equipments[index] = item;
    this.woiSave();
  }

  private _serialNumberAlreadyScanned(equipmentList: IEquipmentWithScanStatus[], serialNumber: string): boolean {
    const scannedSerialNumbers: string[] = equipmentList
      // Only use the lines that have been scanned (status is not undefined)
      .filter((equipment: IEquipmentWithScanStatus) => !!equipment.scan_status)
      // We want the list of serial numbers
      .map((equipment: IEquipmentWithScanStatus) => equipment.serial_number);

    return scannedSerialNumbers.includes(serialNumber);
  }

  /**
   * The user scanned a serial number, add it to the list, either by search result or by manual selection
   */
  public async searchForSerial(): Promise<void> {
    this.f.controls.serialNumber.markAsTouched();
    // There was no serial number to search for, do nothing.
    if (!this.serialNumber) {
      this.toastr.error(`Aucun numéro de série scanné, il n'y a rien à rechercher.`);
      return;
    }
    const equipmentList: IEquipmentWithScanStatus[] = this.woi.metadata.equipments || [];

    // If alredy scanned, we do nothing
    if (this._serialNumberAlreadyScanned(equipmentList, this.serialNumber)) {
      this.toastr.error(`L'équipement correspondant au numéro de série ${this.serialNumber} a déjà été ajouté.`);
      return;
    }

    // If the serial number matches one that's waiting to be scanned, no need to search just update the status
    const existingMatchIndex: number = this._findUnscannedEquipmentIndexBySerialNumber(equipmentList, this.serialNumber);
    if (existingMatchIndex >= 0) {
      // Update the line's status
      this.woi.metadata.equipments[existingMatchIndex].scan_status = 'scanned';
      // Update the display
      this._refreshEquipmentItems();
      this._refocusAndResetInput();
      // Don't forget to save the updated list (this is not sync, so we do this last)
      this.woiSave();
      return;
    }

    // Otherwise we need to search for it
    this.loading = true;
    this._searchBySerial(this.serialNumber)
      .then(async (foundEquipment: IEquipment | undefined) => {
        // The search either returned an equipment or no match was found,
        // either way we want to add the serial number to the list.
        await this._updateEquipmentLine(foundEquipment, this.serialNumber);
        // Update the display
        this._refreshEquipmentItems();
        this._refocusAndResetInput();
        // Don't forget to save the updated list (this is not sync, so we do this last)
        this.woiSave();
      })
      .catch((message: string | undefined) => {
        // We arrived here because something went wrong with the search since the missing serial number and
        // empty unscanned list were already handled at the start of the method.
        if (message) {
          this.toastr.error(message, 'Recherche Equipement');
        }
      })
      .finally(() => this.loading = false);
  }

  /**
   * Empty and select the input field
   */
  private _refocusAndResetInput(): void {
    // Make sure the WOI is refreshed correctly so that the scanned list gets displayed
    // this.signalsService.broadcast('woi-detail:refresh');
    // Select the serial number input again for the next equipment
    setTimeout(() => this.serialNumberInput?.nativeElement.focus());
    // Reset the serial number field
    this.serialNumber = null;
  }

  /**
   * Search for the serial number and return the matching equipment.
   * If no equipment is found we should return a "new" equipment containing only the serial number.
   * @param serialNumber
   * @private
   */
  private _searchBySerial(serialNumber: string): Promise<IEquipment> {
    return new Promise<IEquipment>((resolve: (value: IEquipment) => void, reject: (reason?: string) => void) => {
      if (!serialNumber) {
        reject();
      }
      this.apiShiva.equipments.list({ serial_search: serialNumber })
        .catch((err: unknown) => {
          console.error(err);
          this.toastr.error('Erreur lors de la recherche. Veuillez essayer à nouveau.');
        })
        .then((listResult: IListResult<IEquipment>) => this._handleSearchResults(listResult, serialNumber))
        .then((equipment: IEquipment) => resolve(equipment))
        .catch((error: string) => reject(error));
    });
  }

  /**
   * Decide what to do with the equipments matching the searched serial number
   */
  private _handleSearchResults(listResult: IListResult<IEquipment>, serialNumber: string): IEquipment {
    if (listResult.count === 0) {
      // Nothing was found, return a new line with the scanned serial number, this means we consider the result to be "truthy"
      return {
        id: undefined,
        serial_number: serialNumber,
      };
    }

    // At least one equipment was found, return the latest (sort by created_at)
    const sorted: IEquipment[] = listResult.results.sort((a: IEquipment, b: IEquipment) => a.created_at > b.created_at ? -1 : 1);
    return sorted[0];
  }

  /**
   * Link the found or selected equipment to the appropriate line in the equipment list.
   * If no equipment lines match the scanned equipment then we add a new line
   */
  private async _updateEquipmentLine(foundEquipment: IEquipment | undefined, serialNumber: string): Promise<void> {
    const tinyEquipment: ITinyEquipment = this._getEquipmentPropertiesForMetadata(foundEquipment);
    const matchIndex: number = await this._findMatchingIndex(tinyEquipment);

    if (matchIndex >= 0) {
      // A match was found, either by name or by selection, update it
      const equipmentLine: IEquipmentWithScanStatus = this.woi.metadata.equipments[matchIndex];
      this.woi.metadata.equipments[matchIndex] = deepMerge(
        equipmentLine,
        tinyEquipment,
        {
          serial_number: serialNumber,
          scan_status: 'scanned',
        } as IEquipmentWithScanStatus,
      );

    } else {
      // No existing line was a match, add a new line (without model or anything...)
      if (!this.woi.metadata.equipments) {
        // In case the list was empty (first scan), initialise it
        this.woi.metadata.equipments = [];
      }
      // Check if the equipment was found using the serial number; if not, open a form to add a new equipment line.
      if (tinyEquipment.code) {
        this.woi.metadata.equipments.push(deepMerge(
        tinyEquipment,
          {
            serial_number: serialNumber,
            scan_status: 'added',
          } as IEquipmentWithScanStatus,
        ));
      } else {
        this.openAddEquipmentLineModal(serialNumber);
      }
    }
  }

  /**
   * Get the relevant equipment information for the metadata
   */
  private _getEquipmentPropertiesForMetadata(equipment: IEquipmentWithScanStatus): ITinyEquipment {
    if (!equipment) {
      return { id: null };
    }
    const model = equipment.model
      ? {
        id: equipment.model.id,
        name: equipment.model.name,
        type_label: equipment.model.type_label,
        brand: {
          name: equipment?.model.brand?.name,
        },
      }
      : null;

    const entity = equipment.entity
      ? {
        code: equipment.entity.code || null,
        name: equipment.entity.name || null,
      }
      : null;

    return {
      id: equipment.id,
      code: equipment.code,
      model,
      serial_number: equipment.serial_number || null,
      entity,
      location: equipment.location || null,
    };
  }

  /**
   * Get the line that will contain the scanned serial number.
   *
   * If there are no unscanned lines, we create a new line.
   * Else if no equipment was found, we ask the user to select one.
   *   If no match was found but there are unscanned lines, we ask the user to select one.
   * Else equipment was found, we try to match it to a line by constructor and model.
   *
   * @returns the index of the matching (or selected) line, -1 if no available lines remain or no match was found
   */
  private async _findMatchingIndex(foundEquipment: IEquipment | undefined): Promise<number> {
    const allEquipments: IEquipmentWithScanStatus[] = (this.woi.metadata.equipments || []);
    // The user can only select lines that have not been scanned AND that don't have a serial number,
    // because if the serial number is present then it would already have been matched.
    const unscannedLines: IEquipmentWithScanStatus[] = allEquipments.filter(
      (equipment: IEquipmentWithScanStatus) => !equipment.scan_status && !equipment.serial_number,
    );

    // If there are no unscanned lines, there will be no match so we return undefined.
    if (!unscannedLines.length) {
      return -1;
    }

    // Something was found (either a single match, or the user selected one from the list), try and match in the list
    const index = this._findUnscannedEquipmentIndexByName(allEquipments, foundEquipment);
    if (index >= 0) {
      return index;
    }

    // Either no match was found or no equipment was found, ask the user to select a line
    const equipmentLine: IEquipmentWithScanStatus | undefined = await this._askUserForSelection(unscannedLines);
    if (equipmentLine) {
      // The user selected something, return it
      // To make sure we return the correct index, find the selected line in the whole list
      return this._findUnscannedEquipmentIndexByName(allEquipments, equipmentLine);
    }

    // If we got to here then the user didn't select anything and no match was found,
    // return invalid index so that the calling method can create a new line
    return -1;
  }

  /**
   * Find the index of the first unscanned line matching the provided target equipment using the constructor and model branded names.
   */
  private _findUnscannedEquipmentIndexByName(equipmentList: IEquipmentWithScanStatus[], target: IEquipmentWithScanStatus | undefined): number {
    return equipmentList.findIndex((equipment: IEquipmentWithScanStatus) => {
      return !equipment.scan_status
        && equipment.model?.branded_name === target?.model?.branded_name
        && equipment.model.brand.name === target?.model?.brand.name;
    });
  }

  /**
   * Ask the user to select one of the provided equipment options.
   * If one is selected the promise resolves with that option.
   * Otherwise, the promise rejects.
   */
  private async _askUserForSelection<T extends IEquipment>(list: IEquipmentWithScanStatus[]): Promise<IEquipmentWithScanStatus | undefined> {
    const modal = this.ngbModal.open(PneyluEquipmentSelectModalComponent, { backdrop: 'static', size: 'lg' });
    modal.componentInstance.equipmentList = list;
    return modal.result
      .then((result: T | undefined) => result)
      .catch(() => undefined);
  }

  /**
   * Find the index of the first unscanned line matching the provided serial number
   */
  private _findUnscannedEquipmentIndexBySerialNumber(equipmentList: IEquipmentWithScanStatus[], serialNumber: string): number {
    return equipmentList.findIndex(
      (equipment: IEquipmentWithScanStatus) => !equipment.scan_status && equipment.serial_number === serialNumber,
    );
  }

  /**
   * Update the list of items to display based on the contents of the WOI metadata.
   */
  private _refreshEquipmentItems(): void {
    this.items = this.woi.metadata.equipments || [];
  }
}
